import { areaById, toHex, type MockupJob, type MockupTemplate, type Rgb } from '@eip/engine';
import type { TemplateStatus } from '../state/mockup';
import type { DocEntry } from '../state/store';
import { sheetLabel } from '../worker/mockup-worker';
import type { EngineApi, MockupFormat } from '../worker/protocol';
import { slug } from './films';
import { describeBackdrop, garmentForColors, skippedPrints } from './mockup-ui';
// `ReportMockup` is imported for its TYPE only: report.ts is imported by films.ts,
// which this module imports `slug` from, so a value import either way round would
// close a module cycle. The report builder lives here (it needs the registry and
// the file names) and hands report.ts a plain array.
import type { ReportMockup } from './report';
import { candidatesFor, snapGarment, type TemplateSource } from './templates';
import { uniqueName, type createZip } from './zip';

/** The worker calls a mockup export needs. Mirrors `FilmsDeps`: the two engine
 *  methods, plus the optional progress/clock hooks the ZIP paths use. */
export interface MockupDeps {
  mockupExport: EngineApi['mockupExport'];
  mockupSheet: EngineApi['mockupSheet'];
  /** Prepare one blank in the worker, resolving only once it is ready. An export
   *  renders blanks the CANVAS never showed — every colourway but the one on
   *  screen, and the style's back blank — and the worker holds only what has been
   *  prepared, so each distinct template is put through this first. Optional so a
   *  test (or a caller with a pre-prepared worker) can leave it out. */
  ensureTemplate?(id: string): Promise<void>;
  /** Decode one backdrop scene in the worker, resolving only once its pixels are there. The same
   *  contract, and the same reason, as `ensureTemplate`: a scene is a Blob in IndexedDB until a
   *  render names it, and an export whose job carries one must wait for it exactly as it waits for
   *  the blank — or the composite fails with the worker's own "scene not loaded". */
  ensureScene?(id: string): Promise<void>;
  /** Called as each colourway lands, with the number written so far and the total. */
  onProgress?(done: number, total: number): void;
  now?: Date;
}
/** Only the job ticket's thumbnail grid needs this one, so it is asked for separately. */
export interface ThumbDeps {
  mockupThumb: EngineApi['mockupThumb'];
  ensureTemplate?(id: string): Promise<void>;
  ensureScene?(id: string): Promise<void>;
  /** Called as each thumbnail lands: a ticket for eight colourways is eight renders. */
  onProgress?(done: number, total: number): void;
}

/** One colourway: the job to render, the caption it carries and the blank it lands on. */
export interface MockupPlan { job: MockupJob; label: string; template: MockupTemplate }
/** A plan plus the file name an export would write it under. */
export interface NamedMockupPlan extends MockupPlan { file: string }

const ext = (format: MockupFormat) => (format === 'jpeg' ? 'jpg' : 'png');
const at = (prefix: string, name: string) => (prefix ? `${prefix}/${name}` : name);

/** `Skull <Job>.png` → `Skull <Job>`; the file names slug it from there. */
export function mockupBaseName(name: string): string { return name.replace(/\.[^.]+$/, ''); }

/**
 * `skull-job_mockup_black-flat_white.png` — job, blank, colourway. Every part is
 * slugged (these land on case-insensitive volumes and get typed by hand at the
 * press, exactly like the film names), and each has its own fallback so a name
 * that slugs away to nothing still produces a usable entry.
 */
export function mockupFileName(base: string, templateName: string, colorLabel: string, format: MockupFormat): string {
  return `${slug(base) || 'job'}_mockup_${slug(templateName) || 'template'}_${slug(colorLabel) || 'colour'}.${ext(format)}`;
}
/** The one-image contact sheet of every selected colourway. */
export function sheetFileName(base: string, format: MockupFormat): string { return `${slug(base) || 'job'}_colourways.${ext(format)}`; }
/** The two-tile front/back pair. */
export function frontBackFileName(base: string, format: MockupFormat): string { return `${slug(base) || 'job'}_front-back.${ext(format)}`; }

/** The blank a document's job renders onto, or `undefined` when it names one the
 *  registry no longer holds (a deleted user template, a redeploy that dropped it). */
function templateOf(doc: DocEntry, sources: TemplateSource[]): MockupTemplate | undefined {
  const id = doc.mockup?.job.templateId;
  return id ? sources.find((s) => s.template.id === id)?.template : undefined;
}

/** True when `doc` has a mockup this module can export; the batch skips the rest. */
export function hasMockup(doc: DocEntry, sources: TemplateSource[]): boolean { return !!templateOf(doc, sources); }

/**
 * Prepare every DISTINCT blank a set of plans renders — and decode every distinct backdrop scene
 * they name — in order, before any of them is sent to the worker: a colourway sheet fails on its
 * first tile rather than after a minute of compositing, and — more to the point — the blanks a
 * sheet uses are mostly ones no canvas has ever asked for, so without this the worker has never
 * been handed their photos.
 *
 * The scenes for the same reason: the worker holds a scene's pixels only once something has asked
 * it to decode them, and while the panel's own preview does that for the job on screen, an export
 * racing a scene the shop just deleted-and-re-imported (or a batch of documents whose scene no
 * canvas has shown) would otherwise reach `renderMockup` with nothing to cover the photo with.
 * One scene per job in practice — every colourway of a job shares its backdrop — but deduplicated
 * all the same.
 */
async function prepareAll(
  plans: ReadonlyArray<{ template: MockupTemplate; job?: MockupJob }>,
  deps: { ensureTemplate?: MockupDeps['ensureTemplate']; ensureScene?: MockupDeps['ensureScene'] },
): Promise<void> {
  if (deps.ensureTemplate) {
    const seen = new Set<string>();
    for (const { template } of plans) {
      if (seen.has(template.id)) continue;
      seen.add(template.id);
      try {
        await deps.ensureTemplate(template.id);
      } catch (e) {
        throw new Error(`template "${template.name}" could not be prepared: ${(e as Error).message}`);
      }
    }
  }
  if (!deps.ensureScene) return;
  const loaded = new Set<string>();
  for (const { job } of plans) {
    const backdrop = job?.backdrop;
    if (backdrop?.kind !== 'scene' || loaded.has(backdrop.sceneId)) continue;
    loaded.add(backdrop.sceneId);
    try {
      await deps.ensureScene(backdrop.sceneId);
    } catch (e) {
      // The scene's own name is not here (a job carries the id alone), and there is one backdrop
      // per job, so the reason is what the message is for.
      throw new Error(`the backdrop scene could not be loaded: ${(e as Error).message}`);
    }
  }
}

/**
 * The blank a front+back pair prints the back design into: a BACK-view template
 * of the front blank's style, preferring one already photographed in `wanted`
 * (the colour the front tile is wearing) so the pair needs no recolouring, else
 * the style's first back blank. `undefined` when the style has none.
 *
 * The back DOCUMENT's own `job.templateId` is not it: that is a front blank (the
 * only kind the panel offers), the one its preview is rendered on.
 */
export function backBlankFor(sources: TemplateSource[], front: MockupTemplate, wanted: Rgb = front.garmentColor): MockupTemplate | undefined {
  const backs = candidatesFor(sources, front.style, 'back');
  // `snapGarment`'s ΔE rather than an exact hex: the front and the back of a
  // style are two different photographs of the same garment, so their measured
  // colours differ by a hair — and an exact match sent the pair off to recolour
  // a blank to a value it already wore.
  return snapGarment(toHex(wanted), backs).snappedTo ?? backs[0];
}

/** True when `hex` is (near enough) the blank's own photographed colour. */
const isOwnColor = (hex: string, t: MockupTemplate) => snapGarment(hex, [t]).snappedTo !== null;

/**
 * The job the BACK half of a pair renders: the back document's own placement,
 * ink look and realism, moved onto the style's back blank in the colour the
 * FRONT tile wears. `undefined` when the style has no back blank at all.
 *
 * One derivation, three readers — the session writes it onto the front
 * document's mockup (`DocMockup.backJob`), the runner renders it into the
 * document's back preview slot, and `exportFrontBack` prints it. Deriving it
 * twice is how the viewer's "Back" toggle came to show the back design on a
 * FRONT blank while the export put it on the back one.
 *
 * `backJob` falls back to the FRONT document's job when the back document has
 * none of its own (it has not been seeded yet): the pair is still one garment
 * photographed twice, so the front's placement is the better guess than nothing.
 */
export function backJobFor(
  sources: TemplateSource[], front: MockupTemplate, frontJob: MockupJob, backDocJob: MockupJob | undefined,
): MockupJob | undefined {
  const wanted = frontJob.garment ?? front.garmentColor;
  const backTemplate = backBlankFor(sources, front, wanted);
  if (!backTemplate) return undefined;
  // The chosen blank may already be photographed in this colour (`backBlankFor`
  // prefers one that is), and one that cannot be recoloured at all keeps its own
  // — the worker would force that anyway, and asking for it here says so.
  const own = isOwnColor(toHex(wanted), backTemplate);
  return {
    ...(backDocJob ?? frontJob),
    templateId: backTemplate.id,
    garment: own || backTemplate.recolorable === false ? null : wanted,
    // The FRONT job's backdrop, never the back document's own: the pair is one garment on one
    // surface, photographed twice, and a back tile on the shop's studio sweep beside a front tile
    // on a concrete wall is not a pair. The back document's own panel keeps its own backdrop for
    // its own preview, which is what `backDocJob` is for everything else here.
    //
    // MINOR (final review 10, note only): the back render correctly IGNORES this on a back blank
    // whose mask does not separate, but the inspector's Backdrop row and the ticket describe the
    // FRONT job only (the same front-only shape as the carried-forward Clipped row), so that is
    // unannounced. Unreachable until a style ships a back photo.
    backdrop: frontJob.backdrop ?? null,
  };
}

/**
 * The colourways `colors` asks for, resolved against the document's current
 * template — one job per colour, in the order they were selected.
 *
 * Each colour goes through `garmentForColors` exactly as the panel's preview
 * does, so what a file shows is what the canvas showed: a colour a blank of the
 * style already wears switches the job onto that blank (`garment: null`),
 * anything else recolours the current one.
 *
 * The one place an export differs from the preview is a NON-recolourable blank:
 * the panel keeps its own colour on screen (recolouring it would smear ink
 * across the backdrop) and says so, but writing that out as `…_red.png` would
 * hand the shop a file whose name lies about its contents. Those colours are
 * left out and named in `skipped` instead.
 *
 * No colours selected is one job in the blank's own colour — the same thing the
 * canvas is showing.
 */
export function jobsForColors(doc: DocEntry, sources: TemplateSource[], colors: ReadonlyArray<string>): { jobs: MockupPlan[]; skipped: string[] } {
  const base = templateOf(doc, sources);
  const current = doc.mockup?.job;
  if (!base || !current) return { jobs: [], skipped: [] };
  const byId = new Map(sources.map((s) => [s.template.id, s.template]));
  if (colors.length === 0) {
    const job: MockupJob = { ...current, templateId: base.id, garment: null };
    return { jobs: [{ job, label: sheetLabel(job, base), template: base }], skipped: [] };
  }
  const jobs: MockupPlan[] = [];
  const skipped: string[] = [];
  for (const hex of colors) {
    const { templateId, garment } = garmentForColors([hex], base, sources);
    const template = byId.get(templateId) ?? base;
    // `garmentForColors` returns the base blank in its OWN colour both when the
    // hex snapped to it and when the blank simply cannot be recoloured. Only the
    // second is a colour we are failing to deliver.
    if (!garment && template.id === base.id && base.recolorable === false && !isOwnColor(hex, base)) {
      skipped.push(`${hex}: no recolourable blank`);
      continue;
    }
    const job: MockupJob = { ...current, templateId, garment };
    jobs.push({ job, label: sheetLabel(job, template), template });
  }
  return { jobs, skipped };
}

/** `jobsForColors` over the document's OWN selection, with the file name each
 *  plan would be written under. One place computes those names, so the ticket,
 *  the thumbnails and the ZIP always agree. */
export function mockupPlans(doc: DocEntry, sources: TemplateSource[], format: MockupFormat): { plans: NamedMockupPlan[]; skipped: string[] } {
  const { jobs, skipped } = jobsForColors(doc, sources, doc.mockup?.colors ?? []);
  const base = mockupBaseName(doc.name);
  // Two colours that slug to the same name (`#FF0000` selected twice over, or two
  // blanks a shop named alike) would otherwise write one ZIP entry over the other
  // and leave the ticket naming a file that is not there.
  const taken = new Set<string>();
  return { plans: jobs.map((p) => ({ ...p, file: uniqueName(mockupFileName(base, p.template.name, p.label, format), taken) })), skipped };
}

/** The document's mockup, or a throw naming the document — every export path
 *  starts here so the message is the same wherever it surfaces. */
function requireMockup(doc: DocEntry, sources: TemplateSource[]): { job: MockupJob; template: MockupTemplate } {
  const template = templateOf(doc, sources);
  const job = doc.mockup?.job;
  if (!template || !job) throw new Error(`${doc.name}: no mockup set up — choose a template in the mockup panel first`);
  return { job, template };
}

/**
 * The document's CURRENT job — the colour on screen — at the photo's full resolution.
 *
 * `skipped` is the render's own: the prints that did NOT make it into the file, each as
 * `"<Area>: <reason>"` — the same `name: reason` shape the colourway skips use, so the export
 * bar's note can say it without knowing which kind of skip it is holding. A file that is quietly
 * missing a sleeve print is the one thing a mockup export must not hand over unannounced.
 */
export async function exportMockup(
  doc: DocEntry, sources: TemplateSource[], deps: MockupDeps, format: MockupFormat,
): Promise<{ blob: Blob; filename: string; clipped: boolean; skipped: string[] }> {
  const { job, template } = requireMockup(doc, sources);
  await prepareAll([{ template, job }], deps);
  const result = await deps.mockupExport(doc.id, job, format);
  return {
    // TS 5.9's lib.dom narrows ArrayBufferView<ArrayBuffer>, so a plain Uint8Array
    // no longer structurally satisfies BlobPart without this cast.
    blob: new Blob([result.bytes as BlobPart], { type: result.mime }),
    filename: mockupFileName(mockupBaseName(doc.name), template.name, sheetLabel(job, template), format),
    clipped: result.clipped,
    skipped: skippedPrints(result.skipped, template).map((s) => `${s.name}: ${s.reason}`),
  };
}

/**
 * Every selected colourway on one captioned contact sheet. The tiling happens in
 * the worker (`mockupSheet`), which composites one colourway at a time — the
 * main thread never holds more than the finished sheet.
 */
export async function exportColourways(
  doc: DocEntry, sources: TemplateSource[], deps: MockupDeps, format: MockupFormat, tileW: number, labels = true,
): Promise<{ blob: Blob; filename: string; skipped: string[] }> {
  requireMockup(doc, sources);
  const { jobs, skipped } = jobsForColors(doc, sources, doc.mockup?.colors ?? []);
  // One tile is not a sheet — that is what the "Mockup PNG" button is for. The
  // count is taken AFTER the skips, so two colours of which one cannot be worn
  // fails here rather than producing a one-tile "colourway sheet"; the reasons
  // travel with the message, or the shop sees a refusal it cannot explain.
  if (jobs.length < 2) {
    throw new Error(`select at least two colours for a colourway sheet${skipped.length ? ` (dropped: ${skipped.join(', ')})` : ''}`);
  }
  await prepareAll(jobs, deps);
  deps.onProgress?.(0, jobs.length);
  // An empty label list is the worker's "no caption bar at all" (`renderSheet`
  // takes `null` there), which is what the panel's Labels toggle turns off.
  const result = await deps.mockupSheet(doc.id, jobs.map((j) => j.job), labels ? jobs.map((j) => j.label) : [], format, tileW);
  deps.onProgress?.(jobs.length, jobs.length);
  return { blob: new Blob([result.bytes as BlobPart], { type: result.mime }), filename: sheetFileName(mockupBaseName(doc.name), format), skipped };
}

/** The default tile width for a front/back pair, matching `renderSheet`'s own. */
export const FRONT_BACK_TILE_W = 800;

/**
 * The front document's job beside the back document's, as one two-tile sheet.
 *
 * The back design is a document in its own right, carrying its own placement and
 * ink look — so the two tiles composite DIFFERENT artwork, which is why
 * `mockupSheet` takes a `docIds` list: one worker call still, but each job names
 * the document whose output it prints.
 *
 * The back tile's TEMPLATE is not the back document's own, though: that is the
 * front blank its preview sits on. The pair renders it into the style's back
 * blank, wearing the colour the front tile wears — so a front+back pair reads
 * as one garment photographed twice, which is the whole point of it.
 *
 * That job is NOT recomputed here: it is `doc.mockup.backJob`, the one the
 * session derived (`backJobFor`) and the viewer's "Back" toggle has been
 * showing all along. One derivation is what keeps the preview and the file the
 * same picture.
 */
export async function exportFrontBack(
  doc: DocEntry, backDoc: DocEntry, sources: TemplateSource[], deps: MockupDeps, format: MockupFormat, tileW = FRONT_BACK_TILE_W, labels = true,
): Promise<{ blob: Blob; filename: string }> {
  const front = requireMockup(doc, sources);
  requireMockup(backDoc, sources);
  const backJob = doc.mockup?.backJob;
  if (!backJob) throw new Error(`${front.template.name}: this style has no back template`);
  const backTemplate = sources.find((s) => s.template.id === backJob.templateId)?.template;
  if (!backTemplate) throw new Error(`${front.template.name}: this style has no back template`);
  await prepareAll([{ template: front.template, job: front.job }, { template: backTemplate, job: backJob }], deps);
  deps.onProgress?.(0, 2);
  const result = await deps.mockupSheet(doc.id, [front.job, backJob], labels ? ['Front', 'Back'] : [], format, tileW, [doc.id, backDoc.id]);
  deps.onProgress?.(2, 2);
  return { blob: new Blob([result.bytes as BlobPart], { type: result.mime }), filename: frontBackFileName(mockupBaseName(doc.name), format) };
}

/**
 * Writes one document's colourways into an open ZIP under `prefix/`, one entry
 * at a time as the worker encodes them — the same streaming discipline
 * `addFilmsToZip` follows, for the same reason: a full-size mockup is megabytes,
 * and a document with eight colourways would otherwise hold all eight at once on
 * top of the copies the ZIP writer keeps.
 *
 * Throws when the document has no mockup at all (the batch records that as a
 * skip); a single colour that could not be rendered is returned in `skipped`
 * rather than throwing, so the rest of the document still lands.
 */
export async function addMockupsToZip(
  zip: ReturnType<typeof createZip>, doc: DocEntry, sources: TemplateSource[], deps: MockupDeps, prefix: string, format: MockupFormat,
): Promise<{ count: number; skipped: string[] }> {
  requireMockup(doc, sources);
  const { plans, skipped } = mockupPlans(doc, sources, format);
  await prepareAll(plans, deps);
  const now = deps.now ?? new Date();
  let count = 0;
  for (const plan of plans) {
    const result = await deps.mockupExport(doc.id, plan.job, format);
    zip.add(at(prefix, plan.file), result.bytes, { mtime: now });
    count++;
    deps.onProgress?.(count, plans.length);
  }
  return { count, skipped };
}

/**
 * The job ticket's mockup rows: one per colourway an export would produce, with
 * the file name it would carry so the ticket and the archive line up.
 *
 * `undefined` — no section at all — when the document has no mockup, when its
 * blank has left the registry, or when `templateStatus` says the blank is not
 * prepared: listing colourways nobody can render describes a job that cannot be
 * delivered. Pass no status map and a template still in the registry is enough —
 * which is what a test (or any caller with no prepared blanks to check against)
 * does; both shipped callers, the single-document ticket and the batch's, pass one,
 * because `applied` is measured from it.
 *
 * `docs` and `scenes` are the two lists a row can only be WRITTEN with: the document an extra
 * print names, and the scene a backdrop names. Both are optional and both behave the same way
 * without one — the row says what it can stand behind rather than printing an id.
 */
export function mockupReport(
  doc: DocEntry, sources: TemplateSource[], format: MockupFormat, templateStatus?: Record<string, TemplateStatus>,
  docs?: ReadonlyArray<{ id: string; name: string }>,
  scenes?: ReadonlyArray<{ id: string; name: string }>,
): ReportMockup[] | undefined {
  const template = templateOf(doc, sources);
  if (!template || !doc.mockup) return undefined;
  if (templateStatus && templateStatus[template.id]?.state !== 'ready') return undefined;
  const { plans } = mockupPlans(doc, sources, format);
  if (plans.length === 0) return undefined;
  // A print of another document's art is only describable if that document is still open to be
  // named; `null` covers both "this document's own art" and "no longer open", because neither is
  // a file name the press could be sent looking for.
  const nameOf = (docId: string | null): string | null => (docId === null ? null : docs?.find((d) => d.id === docId)?.name ?? null);
  return plans.map((p) => ({
    template: p.template.name,
    view: p.template.view,
    // `sheetLabel`, the same caption the sheet prints and the file name carries:
    // the blank's colour name, or the hex when the garment is being recoloured.
    colorName: p.label,
    hex: toHex(p.job.garment ?? p.template.garmentColor),
    look: p.job.look,
    // What is BEHIND the garment, in the one line the inspector shows: the studio backdrop the
    // photo came with (`none`), a colour, a gradient, or one of the shop's own scene photographs.
    // Named against the scene list the caller holds, so a scene that has been deleted reads as
    // missing rather than as a UUID nobody at the press can act on.
    //
    // Per ROW, and against that row's own blank: a job keeps its backdrop when it moves onto a blank
    // whose mask does not separate (so going back to a flat-lay restores the swap), but the render
    // ignores it there — and the ticket must describe the file that will be made, not the job.
    backdrop: describeBackdrop(p.job.backdrop ?? null, scenes ?? [], {
      applied: templateStatus?.[p.template.id]?.prepared?.separable !== false,
    }),
    placement: p.job.placement,
    rotationDeg: p.job.rotationDeg ?? 0,
    extraPrints: (p.job.extraPrints ?? []).filter((x) => x.enabled).map((x) => {
      const area = areaById(p.template, x.areaId);
      return {
        areaId: x.areaId,
        // What the blank calls the area, falling back to the id: `sleeve` is what the JSON keys on,
        // but "Left sleeve" is what the press reads.
        areaName: area?.name ?? x.areaId,
        // Decided HERE, against the blank this row is for, because it is what makes a skip
        // readable: an area the blank has printed nothing for want of a design, one it does not
        // have was retained from another blank.
        areaOnBlank: !!area,
        docName: nameOf(x.docId), placement: x.placement, rotationDeg: x.rotationDeg ?? 0,
      };
    }),
    // The front render's verdict. Like `clipped`, it is a property of the job against the blank's
    // areas, which every colourway of the style shares.
    skipped: doc.mockup?.skipped ?? [],
    file: p.file,
    // The last render's flag. Clipping is a property of the art against the
    // print area, which every blank of a style shares, so it holds for the
    // colourways as well as for the one on screen.
    clipped: doc.mockup?.clipped ?? false,
  }));
}

/** Chunked so a multi-megabyte buffer cannot blow the argument limit of `String.fromCharCode`. */
function base64(bytes: Uint8Array): string {
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(s);
}

/**
 * A small JPEG per colourway, keyed by the file name the report lists it under,
 * as a `data:` URL — the ticket has to be one self-contained file that survives
 * being emailed, so the images are inlined rather than linked.
 *
 * Fetched separately from `buildReport` (which stays synchronous) and one at a
 * time: each is a full-resolution composite in the worker before it is scaled
 * down, so they cost what a render costs.
 */
export async function reportThumbs(
  doc: DocEntry, deps: ThumbDeps, jobs: ReadonlyArray<NamedMockupPlan>, maxDim = 320,
): Promise<Record<string, string>> {
  await prepareAll(jobs, deps);
  const out: Record<string, string> = {};
  let done = 0;
  for (const { job, file } of jobs) {
    out[file] = `data:image/jpeg;base64,${base64(await deps.mockupThumb(doc.id, job, maxDim))}`;
    deps.onProgress?.(++done, jobs.length);
  }
  return out;
}
